import React from 'react';
import { AlertCircle, Eye, MessageCircle } from 'lucide-react';
import { PT } from './types';

interface PTCardProps {
  pt: PT;
  onViewDetails?: (pt: PT) => void;
  onViewComments?: (pt: PT) => void;
  compact?: boolean;
}

const PTCard: React.FC<PTCardProps> = ({ pt, onViewDetails, onViewComments }) => {
  const progressPercentage = pt.progress_percentage || 0;
  const completeCount = pt.complete_count || 0;
  const totalTasks = pt.total_tasks || 0;
  const teamMembers = pt.team_members || [];
  const sections = pt.section_progress || [];

  const getProgressColor = (percentage: number): string => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    if (percentage >= 20) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getProgressTextColor = (percentage: number): string => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    if (percentage >= 20) return 'text-orange-600';
    return 'text-red-600';
  };

  const getSectionEmoji = (percentage: number): string => {
    if (percentage === 100) return '✅';
    if (percentage >= 80) return '🟢';
    if (percentage >= 50) return '🟡';
    if (percentage >= 20) return '🟠';
    return '🔴';
  };

  const getRiskLevelColor = (riskLevel: string): string => {
    if (riskLevel.includes('HIGH')) return 'bg-red-100 text-red-800';
    if (riskLevel.includes('LOW')) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  const criticalAlerts = pt.alerts?.filter(
    (alert) => alert.severity === 'critical' || alert.severity === 'high'
  ) || [];

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 text-white">
        <div className="flex justify-between items-start mb-1">
          <h3 className="font-bold text-lg leading-tight flex-1" title={pt.name}>
            {pt.name.length > 30 ? pt.name.substring(0, 30) + '...' : pt.name}
          </h3>
          <div className="flex gap-1 ml-2">
            <button
              onClick={() => onViewDetails?.(pt)}
              className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition"
              title="View Details"
            >
              <Eye size={20} />
            </button>
            <button
              onClick={() => onViewComments?.(pt)}
              className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition"
              title="View Comments"
            >
              <MessageCircle size={20} />
            </button>
          </div>
        </div>
        {/* Team Leader - check for 3 main team leaders */}
        {(() => {
          const mainLeaders = ['Nabusfanando', 'Lativa Nurunnisa', 'Weni Eka Putri'];
          const teamLeader = teamMembers.find(m =>
            mainLeaders.some(leader => m.name.toLowerCase().includes(leader.toLowerCase()))
          );
          if (teamLeader) {
            // Extract just the name without credentials
            const leaderName = mainLeaders.find(leader =>
              teamLeader.name.toLowerCase().includes(leader.toLowerCase())
            );
            return (
              <p className="text-xs text-blue-100 mb-2">
                TIM - {leaderName}
              </p>
            );
          }
          return null;
        })()}
        <div className="flex justify-between items-center text-sm">
          <span className="bg-white bg-opacity-20 px-2 py-1 rounded">
            {pt.year}
          </span>
          {pt.risk_level && pt.risk_level !== 'A110 Not Filled' && (
            <span className={`px-2 py-1 rounded text-xs font-semibold ${getRiskLevelColor(pt.risk_level)}`}>
              {pt.risk_level.includes('HIGH') ? 'HIGH' : pt.risk_level.includes('LOW') ? 'LOW' : 'MEDIUM'}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Overall Progress */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Overall Progress</span>
            <span className={`text-2xl font-bold ${getProgressTextColor(progressPercentage)}`}>
              {progressPercentage.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden mb-1">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(progressPercentage)}`}
              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
            />
          </div>
          <div className="text-xs text-gray-600 text-right">
            {completeCount}/{totalTasks} tasks complete
          </div>
        </div>

        {/* Section Breakdown */}
        {sections.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-700 mb-2">📊 Section Progress</p>
            <div className="space-y-2">
              {sections.map((section, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-lg">{getSectionEmoji(section.percentage)}</span>
                  <div className="flex-1">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700 truncate" title={section.name}>
                        {section.name}
                      </span>
                      <span className="text-gray-600 ml-2 whitespace-nowrap">
                        {section.percentage.toFixed(0)}% ({section.complete}/{section.total})
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-2 rounded-full transition-all ${getProgressColor(section.percentage)}`}
                        style={{ width: `${Math.min(section.percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Alerts */}
        {criticalAlerts.length > 0 && (
          <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded mb-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="text-red-600" size={16} />
              <p className="text-xs font-semibold text-red-800">
                {criticalAlerts.length} Alert{criticalAlerts.length > 1 ? 's' : ''}
              </p>
            </div>
          </div>
        )}

        {/* Team Members (Compact) */}
        {teamMembers.length > 0 && (
          <div className="bg-blue-50 rounded-lg p-2">
            <p className="text-xs text-gray-600">
              👤 {teamMembers[0].name} ({teamMembers[0].role})
              {teamMembers.length > 1 && ` +${teamMembers.length - 1} more`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PTCard;